import React from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Calendar, BarChart3, Bell, User, Shield, Lock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { LogoIcon } from '../Logo';

const navItems = [
  { to: '/', icon: Home, label: 'Inicio' },
  { to: '/calendar', icon: Calendar, label: 'Calendario' },
  { to: '/stats', icon: BarChart3, label: 'Estadísticas' },
  { to: '/reminders', icon: Bell, label: 'Recordatorios' },
  { to: '/profile', icon: User, label: 'Perfil' },
];

export const Sidebar: React.FC = () => {
  const { user } = useAuth();
  const { canUse } = useSubscription();
  const isAdmin = user?.is_admin || user?.admin_role;

  const allNavItems = isAdmin
    ? [...navItems, { to: '/admin', icon: Shield, label: 'Admin' }]
    : navItems;

  return (
    <aside className="hidden lg:flex fixed top-0 left-0 bottom-0 w-[280px] z-40 flex-col border-r border-[var(--color-border)] bg-[var(--color-surface)]">
      {/* Branding */}
      <div className="px-6 py-6 border-b border-[var(--color-border)]">
        <div className="flex items-center gap-3">
          <LogoIcon size={40} className="rounded-xl flex-shrink-0" />
          <div>
            <h1 className="text-lg font-display font-bold text-[var(--color-text)]">MalDolor</h1>
            <p className="text-xs text-[var(--color-text-muted)]">v2.0</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {allNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]'
                  : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <item.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                <span>{item.label}</span>
                {item.label === 'Recordatorios' && !canUse('reminders') && (
                  <Lock size={12} className="ml-auto opacity-50" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div className="px-4 py-4 border-t border-[var(--color-border)]">
        <div className="flex items-center gap-3 px-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #E8927C 0%, #D4715E 100%)' }}
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--color-text)] truncate">
              {user?.name || 'Usuario'}
            </p>
            <p className="text-xs text-[var(--color-text-muted)] truncate">
              {user?.email || ''}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
};
